import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, AlertCircle, MessageCircle, TrendingDown, Check } from "lucide-react";
import {
    simular,
    formatBRL,
    formatPct,
    PREMISSAS,
    COMISSAO_MIN,
    COMISSAO_MAX,
    COMISSAO_PADRAO,
} from "@/lib/simulator";
import { waLink } from "@/lib/site";
import { trackEvent } from "@/lib/tracking";
import { FadeIn, Eyebrow } from "@/components/Reveal";

const WA_MESSAGE =
    "Olá! Fiz uma simulação no site da Contabeleza e gostaria de uma análise detalhada da minha situação.";

const ATALHOS_FATURAMENTO = ["20000", "50000", "100000"];

const formatDigitosReais = (digits) => {
    if (!digits) return "";
    return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(digits));
};

const Bar = ({ value, max, color, track, delay, reduceMotion }) => {
    const widthPct = Math.max((value / max) * 100, value > 0 ? 3 : 0);
    return (
        <div className={`h-2.5 overflow-hidden rounded-full ${track}`}>
            <motion.div
                className={`h-full rounded-full ${color}`}
                initial={{ width: 0 }}
                animate={{ width: `${widthPct}%` }}
                transition={{ duration: reduceMotion ? 0 : 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
            />
        </div>
    );
};

const ReducaoBadge = ({ pct, className = "" }) =>
    pct > 0 ? (
        <span
            className={`inline-flex items-center gap-1 rounded-full bg-[#22C55E] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[#052E16] ${className}`}
        >
            <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
            menos {pct}%
        </span>
    ) : null;

export default function Simulator() {
    const [faturamento, setFaturamento] = useState("");
    const [percentual, setPercentual] = useState(COMISSAO_PADRAO);
    const reduceMotion = useReducedMotion();

    const r = useMemo(
        () => simular({ faturamento, percentualParceiros: percentual }),
        [faturamento, percentual]
    );

    const reducaoPct =
        r.valido && r.semLei.impostoMensal > 0
            ? Math.round((r.diferencaMensal / r.semLei.impostoMensal) * 100)
            : 0;

    const resumo = r.valido
        ? `Estimativa: sem adequação, ${formatBRL(r.semLei.impostoMensal)} de imposto ao mês, alíquota efetiva de ${formatPct(r.semLei.aliquotaEfetiva)}. Com a Lei do Salão Parceiro, ${formatBRL(r.comLei.impostoMensal)} ao mês, alíquota efetiva de ${formatPct(r.comLei.aliquotaEfetiva)}. Diferença estimada de ${formatBRL(r.diferencaMensal)} ao mês e ${formatBRL(r.diferencaAnual)} ao ano${reducaoPct > 0 ? `, ${reducaoPct}% a menos de imposto` : ""}.`
        : "Informe o faturamento mensal para ver a simulação.";

    return (
        <section
            data-testid="section-simulator"
            aria-labelledby="simulador-titulo"
            className="grain relative overflow-hidden bg-[#08104F] px-5 py-20 sm:px-8 sm:py-28"
        >
            <div className="pointer-events-none absolute inset-0 hero-grid-lines" />
            <div className="relative mx-auto max-w-7xl">
                <FadeIn className="max-w-2xl">
                    <Eyebrow light>Simulador</Eyebrow>
                    <h2
                        id="simulador-titulo"
                        className="mt-4 font-display text-3xl leading-[1.12] tracking-tight text-[#F3EDE6] sm:text-4xl lg:text-5xl"
                    >
                        Simulador de impacto tributário da Lei do Salão Parceiro
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-[#B2D0FF]">
                        Compare a estimativa de impostos de um salão sem adequação com a de um salão
                        adequado à Lei do Salão Parceiro, usando a tabela do Simples Nacional (Anexo III).
                    </p>
                </FadeIn>

                <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
                    <FadeIn>
                        <div className="h-full rounded-3xl border border-white/10 bg-[#12223C]/80 p-7 backdrop-blur-md sm:p-9">
                            <p className="flex items-center gap-2 font-display text-lg text-[#F3EDE6]">
                                <Calculator className="h-5 w-5 text-[#FCB21E]" />
                                Seus números
                            </p>

                            <div className="mt-7 space-y-7">
                                <div>
                                    <label
                                        htmlFor="sim-faturamento"
                                        className="mb-1.5 block text-xs font-semibold text-[#F3EDE6]"
                                    >
                                        Faturamento bruto mensal (R$)
                                    </label>
                                    <input
                                        id="sim-faturamento"
                                        data-testid="input-sim-revenue"
                                        type="text"
                                        inputMode="numeric"
                                        value={formatDigitosReais(faturamento)}
                                        onChange={(e) =>
                                            setFaturamento(e.target.value.replace(/\D/g, "").slice(0, 12))
                                        }
                                        placeholder="Ex.: 50.000"
                                        className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[#F3EDE6] outline-none transition-colors placeholder:text-[#B2D0FF]/40 focus:border-[#B2D0FF]"
                                    />
                                    <div className="mt-2.5 flex flex-wrap gap-2" role="group" aria-label="Valores rápidos de faturamento">
                                        {ATALHOS_FATURAMENTO.map((valor) => {
                                            const ativo = faturamento === valor;
                                            return (
                                                <button
                                                    key={valor}
                                                    type="button"
                                                    data-testid={`btn-sim-revenue-${valor}`}
                                                    aria-pressed={ativo}
                                                    onClick={() => setFaturamento(valor)}
                                                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200 active:scale-[0.97] ${
                                                        ativo
                                                            ? "border-[#FCB21E] bg-[#FCB21E] text-[#08104F]"
                                                            : "border-white/15 bg-white/5 text-[#F3EDE6] hover:border-[#B2D0FF] hover:bg-white/10"
                                                    }`}
                                                >
                                                    R$ {formatDigitosReais(valor)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {faturamento && (
                                        <p className="mt-2 text-[11px] text-[#B2D0FF]/70" aria-hidden="true">
                                            {formatBRL(Number(faturamento))} por mês
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label
                                        htmlFor="sim-percentual"
                                        className="mb-1.5 block text-xs font-semibold text-[#F3EDE6]"
                                    >
                                        Comissão dos profissionais parceiros:{" "}
                                        <span className="text-[#FCB21E]">{percentual}%</span>
                                    </label>
                                    <input
                                        id="sim-percentual"
                                        data-testid="input-sim-partner-percent"
                                        type="range"
                                        min={COMISSAO_MIN}
                                        max={COMISSAO_MAX}
                                        step="5"
                                        value={percentual}
                                        onChange={(e) => setPercentual(e.target.value)}
                                        className="w-full accent-[#FCB21E]"
                                    />
                                    <div className="mt-1 flex justify-between text-[11px] text-[#B2D0FF]/70">
                                        <span>{COMISSAO_MIN}% (mínimo)</span>
                                        <span>{COMISSAO_MAX}% (máximo)</span>
                                    </div>
                                    <p className="mt-2 text-[11px] leading-relaxed text-[#B2D0FF]/70">
                                        Os parceiros recebem {formatBRL(r.parteParceiros)} e a base de
                                        cálculo estimada do salão fica em {formatBRL(r.baseParceriaMensal)}.
                                    </p>
                                </div>

                                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-[11px] leading-relaxed text-[#B2D0FF]/80">
                                    A alíquota sai automaticamente da tabela progressiva do Simples
                                    Nacional (Anexo III), conforme o seu faturamento. Nada de chute.
                                </div>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.12}>
                        <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-[#12223C]/80 p-7 backdrop-blur-md sm:p-9">
                            <p className="font-display text-lg text-[#F3EDE6]">Resultado da simulação</p>
                            <div aria-live="polite" className="sr-only">
                                {resumo}
                            </div>

                            {r.acimaDoLimite ? (
                                <p className="mt-7 flex-1 text-sm leading-relaxed text-[#B2D0FF]" data-testid="simulator-over-limit">
                                    Esse faturamento anual ultrapassa o limite do Simples Nacional. Nesse
                                    porte, a análise precisa ser individual: fale com um especialista da
                                    Contabeleza pelo botão abaixo.
                                </p>
                            ) : r.valido ? (
                                <div className="mt-7 flex flex-1 flex-col gap-4" data-testid="simulator-results">
                                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <p className="text-xs font-medium text-[#B2D0FF]">
                                                Cenário 1: sem adequação, imposto sobre o faturamento total
                                            </p>
                                            <p className="shrink-0 font-display text-lg text-[#F3EDE6]/80">
                                                {formatBRL(r.semLei.impostoMensal)}
                                                <span className="text-xs text-[#B2D0FF]/70">/mês</span>
                                            </p>
                                        </div>
                                        <div className="mt-3">
                                            <Bar
                                                value={r.semLei.impostoMensal}
                                                max={r.semLei.impostoMensal}
                                                color="bg-[#F35E6A]/70"
                                                track="bg-white/10"
                                                delay={0}
                                                reduceMotion={reduceMotion}
                                            />
                                        </div>
                                        <p className="mt-2 text-[11px] text-[#B2D0FF]/60">
                                            {r.semLei.faixa}ª faixa do Anexo III, alíquota efetiva de{" "}
                                            {formatPct(r.semLei.aliquotaEfetiva)} ({formatBRL(r.semLei.impostoAnual)}/ano)
                                        </p>
                                    </div>

                                    <div
                                        data-testid="sim-scenario-2"
                                        className="relative rounded-2xl border-2 border-[#22C55E]/60 p-5 shadow-[0_20px_50px_rgba(34,197,94,0.18)]"
                                    >
                                        <span className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-[#22C55E] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#052E16]">
                                            <Check className="h-3 w-3" aria-hidden="true" />
                                            Com a Lei do Salão Parceiro
                                        </span>
                                        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
                                            <p className="max-w-[16rem] text-sm font-semibold text-[#DCFCE7]">
                                                Cenário 2: comissões dos parceiros fora da base de cálculo
                                            </p>
                                            <div className="text-right">
                                                <p className="font-display text-3xl text-white">
                                                    {formatBRL(r.comLei.impostoMensal)}
                                                    <span className="text-sm text-[#BBF7D0]">/mês</span>
                                                </p>
                                                <ReducaoBadge pct={reducaoPct} className="mt-1.5" />
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <Bar
                                                value={r.comLei.impostoMensal}
                                                max={r.semLei.impostoMensal}
                                                color="bg-gradient-to-r from-[#4ADE80] to-[#16A34A]"
                                                track="bg-[#052E16]/40"
                                                delay={0.15}
                                                reduceMotion={reduceMotion}
                                            />
                                        </div>
                                        <p className="mt-2 text-[11px] text-[#BBF7D0]/80">
                                            {r.comLei.faixa}ª faixa do Anexo III, alíquota efetiva de{" "}
                                            {formatPct(r.comLei.aliquotaEfetiva)} ({formatBRL(r.comLei.impostoAnual)}/ano)
                                        </p>
                                    </div>

                                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-2xl border border-[#22C55E]/30 bg-[#22C55E]/10 p-5">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#86EFAC]">
                                                Diferença estimada no mês
                                            </p>
                                            <p className="mt-1 font-display text-3xl text-[#4ADE80]" data-testid="sim-monthly-diff">
                                                {formatBRL(r.diferencaMensal)}
                                            </p>
                                        </div>
                                        <div className="rounded-2xl border border-[#22C55E]/30 bg-[#22C55E]/10 p-5">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#86EFAC]">
                                                Projeção em 12 meses
                                            </p>
                                            <p className="mt-1 font-display text-3xl text-[#4ADE80]" data-testid="sim-yearly-diff">
                                                {formatBRL(r.diferencaAnual)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-7 flex-1 text-sm leading-relaxed text-[#B2D0FF]" data-testid="simulator-empty">
                                    Informe o faturamento mensal ao lado para comparar os dois cenários de
                                    tributação.
                                </p>
                            )}

                            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                                <p className="flex items-start gap-2 text-xs leading-relaxed text-[#F3EDE6]" data-testid="simulator-disclaimer">
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#FCB21E]" />
                                    Nota: Os valores apresentados são apenas uma estimativa. Entre em contato com
                                    nosso especialista para uma análise detalhada da sua situação.
                                </p>
                            </div>

                            <details className="group mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                                <summary className="cursor-pointer text-xs font-semibold text-[#B2D0FF]">
                                    Premissas utilizadas na simulação
                                </summary>
                                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[11px] leading-relaxed text-[#B2D0FF]/85">
                                    {PREMISSAS.map((p) => (
                                        <li key={p}>{p}</li>
                                    ))}
                                </ul>
                                <p className="mt-3 text-[11px] font-medium text-[#FCB21E]">
                                    Lógica conforme a calculadora oficial da Contabeleza, pendente de validação
                                    final pelo time contábil.
                                </p>
                            </details>

                            <a
                                href={waLink(WA_MESSAGE, "simulador")}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-simulator-whatsapp"
                                onClick={() => trackEvent("whatsapp_click", { location: "simulator" })}
                                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#F3EDE6] px-6 py-3.5 text-sm font-semibold text-[#08104F] transition-all duration-200 hover:bg-white active:scale-[0.98]"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Analisar meu caso com um especialista
                            </a>
                        </div>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
}
